import type { APIClient } from "@heidelbergmaterials/fcl-apiclient"

import {
	InvoiceLineItemShortViewData,
	InvoiceNumberData,
	ProductAllocationKpiStatsData,
	ProductAllocationKpiStatsRequest,
	ProductAllocationSearchRequest,
	InvoiceDeclaration,
	EpdIdData,
	FlagStatusData,
	InvoiceLineItemNumberData,
	FlagProductAllocationRequest,
	ResolveProductAllocationRequest,
	ToggleFlaggedRequest,
} from "./types"

// Product allocation invoice line items
export const getInvoiceLineItems = (
	apiClient: APIClient,
	request: ProductAllocationSearchRequest,
): Promise<InvoiceLineItemShortViewData> =>
	apiClient
		.post("/invoices/product-allocations", request)
		.then(response => response.data)

export const getProductAllocationKpiStats = (
	apiClient: APIClient,
	request: ProductAllocationKpiStatsRequest,
): Promise<ProductAllocationKpiStatsData> =>
	apiClient
		.post("/invoices/product-allocations/kpi", request)
		.then(response => response.data)

export const downloadProductAllocationCsv = (
	apiClient: APIClient,
	request: ProductAllocationKpiStatsRequest,
): Promise<ArrayBuffer> =>
	apiClient
		.post("/invoices/product-allocations/export", request, {
			responseType: "arraybuffer",
		})
		.then(response => response.data)

export const getEpdIds = (apiClient: APIClient): Promise<EpdIdData> =>
	apiClient.get("/invoices/epd-ids").then(response => response.data)

export const getFlagStatuses = (
	apiClient: APIClient,
): Promise<FlagStatusData> =>
	apiClient.get("/invoices/flag-statuses").then(response => response.data)

export const getInvoiceLineItemNumbers = (
	apiClient: APIClient,
): Promise<InvoiceLineItemNumberData> =>
	apiClient
		.get("/invoices/invoice-line-item-numbers")
		.then(response => response.data)

export const getInvoiceNumbers = (
	apiClient: APIClient,
): Promise<InvoiceNumberData> =>
	apiClient.get("/invoices/invoice-numbers").then(response => response.data)

export const getInvoiceDeclaration = (
	apiClient: APIClient,
	id: string,
): Promise<InvoiceDeclaration> =>
	apiClient.get(`/invoices/${id}/declaration`).then(response => response.data)

const TOGGLE_FLAGGED_URL = "/invoices/toggleflagged"

// Builds the multipart/form-data body for POST /invoices/toggleflagged. The
// `filter` is JSON-encoded and files are appended individually; the browser
// (axios) sets the multipart boundary automatically.
const buildToggleFormData = (body: ToggleFlaggedRequest): FormData => {
	const form = new FormData()

	form.append("content", body.content)
	form.append("flagStatus", body.flagStatus)

	if (body.transactionId) {
		form.append("transactionId", body.transactionId)
	}

	if (body.filter) {
		form.append("filter", JSON.stringify(body.filter))
	}

	body.files?.forEach(file => form.append("files", file, file.name))

	return form
}

// POST /invoices/toggleflagged — flags or resolves invoice line items (product
// allocations), selected either by transaction id or by filter (mutually
// exclusive). Sent as multipart/form-data so resolve evidence files can be
// attached. The content-type is set explicitly to override the client's default
// `application/json`; axios then appends the multipart boundary for the
// FormData body. Resolves with the ids of the comments created for the affected
// line items. The author is derived from the auth token server-side, so it is
// never sent in the body.
const toggleFlagged = (
	apiClient: APIClient,
	body: ToggleFlaggedRequest,
): Promise<string[]> =>
	apiClient
		.post(TOGGLE_FLAGGED_URL, buildToggleFormData(body), {
			headers: { "Content-Type": "multipart/form-data" },
		})
		.then(response => response.data)

const buildToggleBody = (
	{ transactionId, filters, comment, files }: ResolveProductAllocationRequest,
	flagStatus: ToggleFlaggedRequest["flagStatus"],
): ToggleFlaggedRequest =>
	filters
		? { content: comment, flagStatus, filter: filters, files }
		: { content: comment, flagStatus, transactionId, files }

export const flagProductAllocations = (
	apiClient: APIClient,
	request: FlagProductAllocationRequest,
): Promise<string[]> =>
	toggleFlagged(apiClient, buildToggleBody(request, "Flagged"))

export const resolveProductAllocations = (
	apiClient: APIClient,
	request: ResolveProductAllocationRequest,
): Promise<string[]> =>
	toggleFlagged(apiClient, buildToggleBody(request, "Resolved"))
